import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import uploadIcon from '@/assets/img/upload.svg';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import moment from 'moment';
import { utcToLocal } from '@/utility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LoadingButton from '@/components/LoadingButton';
import Button from '@/components/CustomButtons/Button';
import ProcessIcon from '@mui/icons-material/Settings';
import { Checkbox, FormControlLabel } from '@mui/material';
import ReconProcessingResultModal from './ReconProcessingResultModal';

const ReconProcessingSmartPayables = ({ smartPayablesAccountId }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadFileText, setUploadFileText] = useState('Choose File');
  const [file, setFile] = useState(null);
  const [shouldDecrypt, setShouldDecrypt] = useState(false);
  const [reconFiles, setReconFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingRecon, setIsProcessingRecon] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  const selectFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setUploadFileText(selectedFile.name);
  };

  const handleUploadFile = (e) => {
    selectFile(e.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    selectFile(e.dataTransfer.files[0]);
  };

  const getReconFiles = async () => {
    if (!smartPayablesAccountId) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/smartpayables/${smartPayablesAccountId}/recon-files`,
      );
      setReconFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching recon files:', error);
      setReconFiles([]);
    }
  };

  useEffect(() => {
    if (smartPayablesAccountId) {
      getReconFiles();
    }
  }, [smartPayablesAccountId]);

  const processReconFile = async () => {
    try {
      setIsLoading(true);

      if (!smartPayablesAccountId) {
        setErrorMessage(
          t('reconProcessingSmartPayables.errors.accountRequired'),
        );
        return;
      }
      if (!file) {
        setErrorMessage(t('reconProcessingSmartPayables.errors.fileRequired'));
        return;
      }

      const confirm = window.confirm(
        t('reconProcessingSmartPayables.confirmProcess'),
      );

      if (!confirm) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('should_decrypt', shouldDecrypt);
      const response = await axios.post(
        `${API_BASE_URL}/smartpayables/${smartPayablesAccountId}/recon/upload`,
        formData,
      );

      setUploadFileText(t('reconProcessingSmartPayables.chooseFile'));
      setErrorMessage('');
      setFile(null);
      setShouldDecrypt(false);
      setProcessResult(response.data || {});
      getReconFiles();
    } catch (e) {
      setErrorMessage(
        e.response?.data?.message ||
          t('reconProcessingSmartPayables.errors.processing'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const processReconOnly = async () => {
    try {
      setIsProcessingRecon(true);

      if (!smartPayablesAccountId) {
        setErrorMessage(
          t('reconProcessingSmartPayables.errors.accountRequired'),
        );
        return;
      }

      const confirm = window.confirm(
        t('reconProcessingSmartPayables.confirmReconProcess'),
      );

      if (!confirm) {
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/smartpayables/${smartPayablesAccountId}/process-recon`,
      );

      if (response.data.success) {
        setErrorMessage('');
        getReconFiles();
      } else {
        setErrorMessage(t('reconProcessingSmartPayables.errors.reconFailed'));
      }
    } catch (e) {
      setErrorMessage(
        e.response?.data?.message ||
          t('reconProcessingSmartPayables.errors.reconProcessing'),
      );
    } finally {
      setIsProcessingRecon(false);
    }
  };

  if (!smartPayablesAccountId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <h3>{t('reconProcessingSmartPayables.noAccountSelected')}</h3>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={<ProcessIcon style={{ color: 'white' }} />}
          onClick={processReconOnly}
          disabled={isProcessingRecon || !smartPayablesAccountId}>
          {isProcessingRecon
            ? t('reconProcessingSmartPayables.processing')
            : t('reconProcessingSmartPayables.processReconButton')}
        </Button>
      </div>

      <Card>
        <CardBody>
          <div>
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '15px',
                border: isDragOver ? '2px dashed #3ab895' : '2px dashed #ddd',
                borderRadius: '6px',
                marginBottom: '15px',
                backgroundColor: isDragOver ? '#f0faf7' : '#f9f9f9',
              }}>
              <input
                type='file'
                onChange={handleUploadFile}
                value=''
                id='reconFile'
                style={{ display: 'none' }}
              />
              <label
                htmlFor='reconFile'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}>
                <img
                  src={uploadIcon}
                  alt='Upload'
                  style={{ marginBottom: '8px', width: '32px', height: '32px' }}
                />
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {uploadFileText ||
                    t('reconProcessingSmartPayables.chooseFile')}
                </p>
              </label>
            </div>

            <div style={{ textAlign: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldDecrypt}
                    onChange={(e) => setShouldDecrypt(e.target.checked)}
                    name='should_decrypt'
                  />
                }
                label={t('reconProcessingSmartPayables.decryptFile')}
              />
            </div>
          </div>

          {errorMessage && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ color: 'red', margin: 0 }}>{errorMessage}</p>
            </div>
          )}

          <div style={{ textAlign: 'center' }}>
            <LoadingButton
              onClick={processReconFile}
              isLoading={isLoading}
              style={{
                marginTop: '15px',
                width: '150px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 20px',
                cursor: 'pointer',
              }}>
              {t('reconProcessingSmartPayables.processButton')}
            </LoadingButton>
          </div>
        </CardBody>
      </Card>

      <Card style={{ marginTop: '20px' }}>
        <CardBody>
          <div style={{ overflow: 'auto' }}>
            <table width='100%' style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.uploadedDate')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.fileName')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.fileStatus')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.uploadedBy')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.totalAmount')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.recordCount')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}>
                    {t('reconProcessingSmartPayables.table.processedDate')}
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      borderBottom: '1px solid #ddd',
                      width: '5%',
                    }}>
                    {t('reconProcessingSmartPayables.table.download')}
                  </th>
                </tr>
              </thead>
              {reconFiles.length > 0 ? (
                <tbody>
                  {reconFiles.map((reconFile, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        {utcToLocal(reconFile.created_at, 'MM/DD/YYYY')}
                      </td>
                      <td style={{ padding: '12px' }}>{reconFile.filename}</td>
                      <td style={{ padding: '12px' }}>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: reconFile.is_processed
                              ? '#4caf50'
                              : '#ff9800',
                            color: 'white',
                            fontSize: '12px',
                          }}>
                          {reconFile.is_processed
                            ? t('reconProcessingSmartPayables.table.processed')
                            : t(
                                'reconProcessingSmartPayables.table.notProcessed',
                              )}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {reconFile.uploaded_by}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {reconFile.processed_amount === null
                          ? t('reconProcessingSmartPayables.table.notAvailable')
                          : `$${parseFloat(reconFile.processed_amount).toFixed(
                              2,
                            )}`}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {reconFile.refunds_processed === null
                          ? t('reconProcessingSmartPayables.table.notAvailable')
                          : reconFile.refunds_processed}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {reconFile.processed_at
                          ? moment(reconFile.processed_at).format(
                              'MM/DD/YYYY HH:mm',
                            )
                          : '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <a
                          style={{ cursor: 'pointer', color: '#1976d2' }}
                          href={
                            API_BASE_URL +
                            '/getFile?filePath=' +
                            reconFile.file_location
                          }>
                          <CloudDownloadIcon color='primary' />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666',
                      }}>
                      {t('reconProcessingSmartPayables.table.noFiles')}
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </CardBody>
      </Card>

      <ReconProcessingResultModal
        result={processResult}
        onClose={() => setProcessResult(null)}
      />
    </div>
  );
};

export default ReconProcessingSmartPayables;
